import { StackDivider, VStack } from '@chakra-ui/react';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { Klass, LexicalEditor, LexicalNode } from 'lexical';
import { FC, useCallback, useMemo, useRef } from 'react';
import { ContentEditable, Toolbar } from './components';
import { ThemeClasses } from './constants';
import { getConverter, HtmlConverter } from './converters';
import { ImageNode } from './nodes';
import { AutoListPlugin, HtmlValuePlugin, ImagePlugin, LinkTooltipPlugin, MarkdownValuePlugin } from './plugins';

export type HtmlEditorProps = {
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  converter?: HtmlConverter | string;
  useMarkdown?: boolean;
  allowImages?: boolean;
  allowLinks?: boolean;
  allowLists?: boolean;
};

export const HtmlEditor: FC<HtmlEditorProps> = ({
  disabled,
  value,
  onChange,
  converter,
  useMarkdown,
  // images are experimental and do not work perfectly yet
  allowImages = false,
  allowLinks = false,
  allowLists = true,
}) => {
  const handleError = useCallback((error: Error, editor: LexicalEditor) => {
    console.error('Unhandled error in editor.', error, editor);
  }, []);

  const htmlConverter = useMemo<HtmlConverter>(() => getConverter(converter), [converter]);
  const prevValue = useRef(value);

  const handleChange = useCallback(
    (newValue: string) => {
      if (newValue !== prevValue.current) {
        prevValue.current = newValue;
        onChange?.(newValue);
      }
    },
    [onChange]
  );

  const nodes = useMemo(() => {
    const result: Klass<LexicalNode>[] = [];
    if (allowImages) {
      result.push(ImageNode);
    }
    if (allowLinks) {
      result.push(LinkNode);
    }
    if (allowLists) {
      result.push(ListNode);
      result.push(ListItemNode);
    }
    return result;
  }, [allowImages, allowLinks, allowLists]);

  return (
    <VStack divider={<StackDivider />} spacing={2} align="stretch" border="default" borderRadius="default">
      <LexicalComposer
        initialConfig={{
          editable: !disabled,
          namespace: 'SimpleHtmlEditor',
          theme: ThemeClasses,
          onError: handleError,
          nodes: nodes,
        }}
      >
        {!disabled && <Toolbar allowImages={allowImages} allowLinks={allowLinks} allowLists={allowLists} />}
        {allowImages && <ImagePlugin />}
        {allowLists && (
          <>
            <ListPlugin />
            <AutoListPlugin />
          </>
        )}
        {allowLinks && (
          <>
            <LinkPlugin />
            <LinkTooltipPlugin />
          </>
        )}
        {useMarkdown ? (
          <MarkdownValuePlugin
            allowImages={allowImages}
            allowLinks={allowLinks}
            allowLists={allowLists}
            value={value}
            onChange={handleChange}
          />
        ) : (
          <HtmlValuePlugin converter={htmlConverter} value={value} onChange={handleChange} />
        )}
        <HistoryPlugin />
        <RichTextPlugin contentEditable={<ContentEditable />} ErrorBoundary={LexicalErrorBoundary} placeholder={null} />
      </LexicalComposer>
    </VStack>
  );
};
